import { useState } from "react";
import { ColumnsType } from "antd/es/table";
import { Button, Flex, MenuProps, Typography } from "antd";
import { useTranslate } from "@refinedev/core";
import dayjs from "dayjs";
import { MediaProjectListItem, MediaDocument } from "pages/media/types";
import { ProjectPill } from "pages/media/projects/components/ProjectPill";
import { ProjectStatusTag } from "pages/media/projects/components/ProjectStatusTag";
import { addDateTz } from "pages/media/utils";
import { FilterValue } from "antd/es/table/interface";
import { ProjectTypeTag } from "pages/media/projects/components/ProjectType/";
import { MenuCTA } from "components/MenuCTA/";
import { DownloadSimpleIcon } from "@phosphor-icons/react";
import { LoadingOutlined } from "@ant-design/icons";
import axiosInstance from "services/api";
import type { ProjectSessionSummary } from "pages/media/home/hooks/useOrganizationAnalytics";
import type { HeaderCellExtraProps } from "./columnPrefs";
import { TableHeader } from "./TableHeader";
import styled from "styled-components";
import { type ProjectFieldColumnKey } from "./projectFieldColumns";

// The subset of PROJECT_FIELD_COLUMNS (the dropdown's source of truth) that
// isn't already a hardcoded column below and needs an overlay column built
// for it, with a value getter and a label.
type AnalyticsFieldColumnKey =
  | "views"
  | "videoReproductions"
  | "documentInteractions"
  | "chatConversations";

const ANALYTICS_FIELD_COLUMN_KEYS: AnalyticsFieldColumnKey[] = [
  "views",
  "videoReproductions",
  "documentInteractions",
  "chatConversations",
];

// Static column widths. `title` intentionally has none — it's the only
// flexible column, so it absorbs whatever space the others don't use.
const ANALYTICS_FIELD_COLUMN_WIDTH: Record<AnalyticsFieldColumnKey, number> = {
  views: 110,
  videoReproductions: 150,
  documentInteractions: 160,
  chatConversations: 150,
};

export const getProjectTypeLabel = (value: MediaProjectListItem) => {
  if (value.type === "flash") return "Autopilot";
  if (value.type === "new_video") return "Custom";
  return value.type
    .replaceAll(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const ProjectActionMenu = ({
  record,
  onEditClick,
  invalidationResource,
}: {
  record: MediaProjectListItem;
  onEditClick: () => void;
  invalidationResource?: string;
}) => {
  const t = useTranslate();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadDocument = async () => {
    if (!record.document_id || !record.organization_id) return;
    setIsDownloading(true);
    try {
      const { data } = await axiosInstance.get<MediaDocument>(
        `media/documents/${record.organization_id}/${record.document_id}`
      );
      if (data.url) {
        globalThis.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Failed to download document", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const extraItems: MenuProps["items"] = record.document_id
    ? [
        {
          key: "download-document",
          label: (
            <Button
              size="small"
              type="text"
              icon={
                isDownloading ? (
                  <LoadingOutlined spin />
                ) : (
                  <DownloadSimpleIcon />
                )
              }
              disabled={isDownloading}
              style={{ background: "transparent" }}
            >
              {record.document_name || t("actions.downloadDocument")}
            </Button>
          ),
          onClick: (e) => {
            e.domEvent.stopPropagation();
            handleDownloadDocument().catch((error) => {
              console.error("Failed to download document", error);
            });
          },
        },
      ]
    : [];

  return (
    <MenuCTA
      onEditClick={onEditClick}
      recordId={record.id}
      invalidationResource={invalidationResource}
      extraItems={extraItems}
    />
  );
};

type ProjectColumnsConfig = {
  t: ReturnType<typeof useTranslate>;
  handleProjectClick: (record: MediaProjectListItem) => void;
  organizationFilterValue?: FilterValue | null;
  theme?: {
    colorLightGrey?: string;
    colorTextDescription?: string;
  };
  invalidationResource?: string;
  summariesMap?: Map<string, ProjectSessionSummary>;
  visibleFieldColumns?: ProjectFieldColumnKey[];
};

export const createProjectColumns = (
  config: ProjectColumnsConfig
): ColumnsType<MediaProjectListItem> => {
  const {
    t,
    handleProjectClick,
    theme,
    invalidationResource,
    summariesMap,
    visibleFieldColumns = [],
  } = config;

  // Builds the `onHeaderCell` factory that injects drag metadata into the
  // custom header cell component (PrefsHeaderCell).
  const headerCell =
    (colKey?: string) => (): React.HTMLAttributes<HTMLElement> =>
      ({
        $colKey: colKey,
      }) as HeaderCellExtraProps as unknown as React.HTMLAttributes<HTMLElement>;

  const fieldColumnValue: Record<
    AnalyticsFieldColumnKey,
    (record: MediaProjectListItem) => number
  > = {
    views: (record) => summariesMap?.get(record.id)?.impression_sessions ?? 0,
    videoReproductions: (record) =>
      summariesMap?.get(record.id)?.play_sessions ?? 0,
    documentInteractions: (record) =>
      summariesMap?.get(record.id)?.document_sessions ?? 0,
    chatConversations: (record) =>
      summariesMap?.get(record.id)?.chat_sessions ?? 0,
  };

  const fieldColumnLabelKey: Record<AnalyticsFieldColumnKey, string> = {
    views: "media.projects.index.views",
    videoReproductions: "media.projects.index.videoReproductions",
    documentInteractions: "media.projects.index.documentInteractions",
    chatConversations: "media.projects.index.chatConversations",
  };

  // Optional analytics field columns, toggled via the "Table fields" filter.
  // Rendered flat (no "Analytics" subheader group) so at most
  // MAX_VISIBLE_PROJECT_FIELD_COLUMNS of them show at once. `title`/`updated`/
  // `type`/`tags`/`status` are also part of PROJECT_FIELD_COLUMNS (the
  // dropdown's source of truth) but already exist as hardcoded columns below
  // — only build overlay columns for the analytics keys, which don't.
  const fieldColumns: ColumnsType<MediaProjectListItem> =
    ANALYTICS_FIELD_COLUMN_KEYS.map((key) => ({
      key,
      title: <TableHeader title={t(fieldColumnLabelKey[key])} />,
      width: ANALYTICS_FIELD_COLUMN_WIDTH[key],
      align: "center" as const,
      hidden: !visibleFieldColumns.includes(key),
      onHeaderCell: headerCell(key),
      render: (_: unknown, record: MediaProjectListItem) => (
        <Typography.Text type="secondary">
          {fieldColumnValue[key](record)}
        </Typography.Text>
      ),
    }));

  return [
    {
      key: "title",
      title: <TableHeader title={t("media.projects.index.name")} />,
      dataIndex: "title",
      sorter: true,
      // No fixed width — it's the one flexible column, absorbing whatever
      // space the fixed-width columns don't use so the table fills its
      // container instead of leaving a gap.
      ellipsis: true,
      onHeaderCell: headerCell("title"),
      render: (_: unknown, record) => (
        <Flex align="center" gap={8} style={{ minWidth: 0 }}>
          <ImageCover src={record.thumbnail_url} />
          <Typography.Text
            ellipsis={{ tooltip: record.title }}
            style={{ fontWeight: 500, flex: 1, minWidth: 0 }}
          >
            {record.title}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      key: "created",
      title: <TableHeader title={t("media.projects.index.createdAt")} />,
      dataIndex: "created",
      sorter: true,
      width: 160,
      hidden: true,
      ellipsis: true,
      onHeaderCell: headerCell("created"),
      render: (_, record) => {
        return (
          <Typography.Text type="secondary">
            {t("media.projects.index.created", {
              time: dayjs(addDateTz(record.created ?? undefined)).fromNow(),
            })}
          </Typography.Text>
        );
      },
      defaultSortOrder: "descend",
    },
    {
      key: "updated",
      title: <TableHeader title={t("media.projects.index.lastModified")} />,
      dataIndex: "updated",
      sorter: true,
      width: 200,
      hidden: !visibleFieldColumns.includes("lastModified"),
      ellipsis: true,
      onHeaderCell: headerCell("updated"),
      render: (_, record) => {
        return (
          <Typography.Text type="secondary">
            {t("media.projects.index.edited", {
              time: dayjs(
                addDateTz(record.updated ?? record.created ?? undefined)
              ).fromNow(),
            })}
          </Typography.Text>
        );
      },
      defaultSortOrder: "descend",
    },
    {
      key: "type",
      title: <TableHeader title={t("media.projects.index.type")} />,
      dataIndex: "type",
      sorter: true,
      width: 160,
      hidden: !visibleFieldColumns.includes("type"),
      ellipsis: true,
      onHeaderCell: headerCell("type"),
      render: (_, record) => <ProjectTypeTag type={record?.type} />,
      defaultSortOrder: "descend",
    },
    ...fieldColumns,
    {
      key: "tags",
      title: <TableHeader title={t("media.projects.index.tags")} />,
      dataIndex: "tags",
      ellipsis: true,
      width: 200,
      hidden: !visibleFieldColumns.includes("tags"),
      onHeaderCell: headerCell("tags"),
      render: (tags: Array<{ tag_key: string; tag_value: string }>) => {
        return (
          <Flex gap={4} wrap="wrap" style={{ maxWidth: "100%" }}>
            {tags?.map((t) => (
              <ProjectPill
                key={t.tag_key}
                {...(theme?.colorLightGrey && { color: theme.colorLightGrey })}
                style={{
                  border: "none",
                  fontWeight: "normal",
                  maxWidth: "100%",
                }}
              >
                <Typography.Paragraph
                  ellipsis={{
                    rows: 1,
                    expandable: false,
                    tooltip: true,
                  }}
                  style={{
                    ...(theme?.colorTextDescription && {
                      color: theme.colorTextDescription,
                    }),
                    margin: 0,
                  }}
                >
                  {t.tag_value || "-"}
                </Typography.Paragraph>
              </ProjectPill>
            ))}
          </Flex>
        );
      },
    },
    {
      key: "status",
      title: <TableHeader title={t("pages.assets.changes.status")} />,
      dataIndex: "status",
      width: 140,
      sorter: true,
      hidden: !visibleFieldColumns.includes("status"),
      ellipsis: true,
      onHeaderCell: headerCell("status"),
      render: (_, record) => {
        if (!record.status) return null;
        return (
          <ProjectStatusTag
            status={record.status}
            style={{
              width: "fit-content",
              maxWidth: "100%",
            }}
          />
        );
      },
    },
    {
      key: "actions",
      dataIndex: "actions",
      width: 60,
      render: (_, record) => {
        return (
          <Flex
            align="center"
            justify="center"
            onClick={(e) => e.stopPropagation()}
          >
            <ProjectActionMenu
              record={record}
              onEditClick={() => handleProjectClick(record)}
              invalidationResource={invalidationResource}
            />
          </Flex>
        );
      },
    },
  ];
};

const ImageCover = styled.div<{ src?: string | null }>`
  width: 56px;
  height: 40px;
  border-radius: 6px;
  background: ${({ src }) =>
    src
      ? `url(${src}) center center / cover no-repeat`
      : "linear-gradient(135deg, rgb(20, 184, 166), rgb(13, 148, 136))"};
`;
